import React, { useState, useEffect, useCallback } from "react";
import { useSearchParams } from "react-router-dom";
import { Navbar } from "@/components/Navbar";
import { Footer } from "@/components/Footer";
import { WhatsAppFloat } from "@/components/WhatsAppFloat";
import { SectionHeading } from "@/components/SectionHeading";
import { CatalogItemCard } from "@/components/CatalogItemCard";
import { Skeleton } from "@/components/ui/skeleton";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Search, X } from "lucide-react";
import { fetchProductsPage, fetchProductsCount } from "@/lib/api";
import { buildWhatsAppLink, categoryEnquireMessage } from "@/lib/whatsapp";

const PAGE_SIZE = 24;

const CATEGORY_FILTERS = [
  { value: "", label: "All Items" },
  { value: "gold", label: "Gold" },
  { value: "silver", label: "Silver" },
  { value: "diamond", label: "Diamond" },
  { value: "others", label: "Others" },
];

const PURITY_FILTERS = [
  { value: "", label: "All Purity" },
  { value: "18K", label: "18K" },
  { value: "22K", label: "22K" },
  { value: "24K", label: "24K" },
];

export default function AllItems() {
  const [searchParams, setSearchParams] = useSearchParams();
  const category = searchParams.get("category") || "";
  const purity = searchParams.get("purity") || "";
  const search = searchParams.get("search") || "";
  const [searchInput, setSearchInput] = useState(search);

  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);

  const loadPage = useCallback(
    async (skip, replace) => {
      const filters = { category: category || undefined, purity: purity || undefined, search: search || undefined };
      const [pageItems, count] = await Promise.all([
        fetchProductsPage({ ...filters, skip, limit: PAGE_SIZE }),
        skip === 0 ? fetchProductsCount(filters) : Promise.resolve(null),
      ]);
      setItems((prev) => (replace ? pageItems : [...prev, ...pageItems]));
      if (count !== null) setTotal(count);
    },
    [category, purity, search]
  );

  useEffect(() => {
    setLoading(true);
    loadPage(0, true).finally(() => setLoading(false));
  }, [loadPage]);

  // Keep the input box in sync if the URL's search param changes elsewhere
  // (e.g. navigating here fresh from the Navbar search).
  useEffect(() => {
    setSearchInput(search);
  }, [search]);

  const handleLoadMore = async () => {
    setLoadingMore(true);
    try {
      await loadPage(items.length, false);
    } finally {
      setLoadingMore(false);
    }
  };

  const updateFilter = (key, value) => {
    const next = new URLSearchParams(searchParams);
    if (value) next.set(key, value);
    else next.delete(key);
    setSearchParams(next);
  };

  const handleSearchSubmit = (e) => {
    e.preventDefault();
    updateFilter("search", searchInput.trim());
  };

  const hasMore = items.length < total;

  return (
    <div className="min-h-screen bg-background text-foreground">
      <Navbar />
      <main className="pt-32 pb-20">
        <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <SectionHeading
            eyebrow="Full Catalog"
            title="All Items"
            subtitle="Browse our complete collection. Search, filter by category and purity — tap any item to see all its photos/videos and enquire on WhatsApp."
          />

          {/* Search */}
          <form onSubmit={handleSearchSubmit} className="max-w-md mx-auto mb-8 relative" data-testid="catalog-search-form">
            <Search className="absolute left-3.5 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground pointer-events-none" />
            <Input
              value={searchInput}
              onChange={(e) => setSearchInput(e.target.value)}
              placeholder="Search by name — e.g. Gold Ring, Kardhani..."
              className="pl-10 pr-10"
              data-testid="catalog-search-input"
            />
            {search && (
              <button
                type="button"
                onClick={() => {
                  setSearchInput("");
                  updateFilter("search", "");
                }}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
                aria-label="Clear search"
                data-testid="catalog-search-clear-btn"
              >
                <X className="h-4 w-4" />
              </button>
            )}
          </form>

          {/* Filters */}
          <div className="flex flex-col gap-4 mb-10">
            <div className="flex flex-wrap justify-center gap-2" data-testid="catalog-category-filters">
              {CATEGORY_FILTERS.map((c) => (
                <button
                  key={c.value || "all"}
                  onClick={() => updateFilter("category", c.value)}
                  className={`rounded-full px-4 py-2 text-sm font-medium border transition-colors ${
                    category === c.value
                      ? "bg-primary text-primary-foreground border-primary"
                      : "bg-card border-border text-foreground/80 hover:border-primary/50"
                  }`}
                  data-testid={`catalog-category-filter-${c.value || "all"}`}
                >
                  {c.label}
                </button>
              ))}
            </div>
            <div className="flex flex-wrap justify-center gap-2" data-testid="catalog-purity-filters">
              {PURITY_FILTERS.map((p) => (
                <button
                  key={p.value || "all"}
                  onClick={() => updateFilter("purity", p.value)}
                  className={`rounded-full px-3.5 py-1.5 text-xs font-medium border transition-colors ${
                    purity === p.value
                      ? "bg-primary/15 text-primary border-primary/40"
                      : "bg-transparent border-border text-muted-foreground hover:border-primary/40"
                  }`}
                  data-testid={`catalog-purity-filter-${p.value || "all"}`}
                >
                  {p.label}
                </button>
              ))}
            </div>
          </div>

          {loading ? (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6 lg:gap-8" data-testid="catalog-loading-state">
              {Array.from({ length: 8 }).map((_, i) => (
                <Skeleton key={i} className="aspect-square rounded-2xl" />
              ))}
            </div>
          ) : items.length === 0 ? (
            <div className="text-center py-20" data-testid="catalog-empty-state">
              <p className="font-heading text-xl text-muted-foreground">
                {search ? `No items match "${search}".` : "No items match these filters yet."}
              </p>
              <Button asChild variant="premium" className="mt-6">
                <a
                  href={buildWhatsAppLink(categoryEnquireMessage("this collection"))}
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  <i className="fa-brands fa-whatsapp" /> Ask on WhatsApp
                </a>
              </Button>
            </div>
          ) : (
            <>
              <p className="text-center text-sm text-muted-foreground mb-6" data-testid="catalog-count-label">
                Showing {items.length} of {total} items
              </p>
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6 lg:gap-8" data-testid="catalog-items-grid">
                {items.map((item) => (
                  <CatalogItemCard key={item.id} item={item} />
                ))}
              </div>

              {hasMore && (
                <div className="flex justify-center mt-12">
                  <Button
                    variant="outline"
                    size="lg"
                    onClick={handleLoadMore}
                    disabled={loadingMore}
                    data-testid="catalog-load-more-btn"
                  >
                    {loadingMore ? "Loading..." : "Load More"}
                  </Button>
                </div>
              )}
            </>
          )}
        </section>
      </main>
      <Footer />
      <WhatsAppFloat />
    </div>
  );
}
